import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, ChartRowDataArray } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { createRowData } from "./common";

interface IGetFileResponceModel {
  /**
   * 入力データのチャネル名の配列
   */
  input: string[];
  /**
   * 入力データのタグ名の配列
   */
  inputtag: string[];
  /**
   * 出力データのタグ名の配列
   */
  output: string[];
  /**
   * 出力データのタグ名の配列
   */
  outputtag: string[];
  /**
   * サンプリング周期[msec]
   */
  interval: number;
  /**
   * 入力データ一覧
   */
  inrows: ChartRowDataArray;
  /**
   * 出力データ一覧
   */
  outrows: ChartRowDataArray;
  /**
   * チャートデータ一覧
   */
  rows: ChartRowDataArray;
}

function convertInData(data: IGetFileResponceModel): ChartRowDataArray {
  // rows
  const datalist: ChartRowDataArray = new Array(data.inrows.length + 1);
  datalist[0] = ["x", ...data.inputtag];
  data.inrows.forEach((row, i) => {
    const idx = i + 1;
    const xdata = i * data.interval / 1000;
    datalist[idx] = [xdata, ...row];
  });
  return datalist;
}

function convertOutData(data: IGetFileResponceModel): ChartRowDataArray {
  // rows
  const datalist: ChartRowDataArray = new Array(data.outrows.length + 1);
  datalist[0] = ["x", ...data.outputtag];
  data.outrows.forEach((row, i) => {
    const idx = i + 1;
    const xdata = i * data.interval / 1000;
    datalist[idx] = [xdata, ...row];
  });
  return datalist;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

export interface IProtData {
  /**
   * ファイルID
   */
  fileid: string;
  /**
   * ファイル名
   */
  fname: string;
  /**
   * データ数
   */
  datanum: number;
  /**
   * サンプリング周期
   */
  interval: number;
  /**
   * Inputチャートデータ
   */
  input: ChartRowDataArray;
  /**
   * Outputチャートデータ
   */
  output: ChartRowDataArray;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからファイルの取得が成功した場合に呼び出されます
   * @callback success
   * @param {IProtData[]} prots - チャートデータ一覧
   * @param {number} status - httpステータス
   */
  success: (prot: IProtData, status: number) => void;
}

interface GetFIleModel {
  /**
   * ファイルID
   */
  fileid: string;
  /**
   * ファイル名
   */
  name: string;
  /**
   * データ数
   */
  datanum: number;
  /**
   * サンプリング周期
   */
  interval: number;
  /**
   * データ間引き閾値
   */
  threshold: number;
  /**
   * 終端を含める場合はtrue,それ以外の場合はfalse
   */
  includeEnd: boolean;
}

interface IAxiosInternalParams extends GetFIleModel, ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const indata = convertInData(res.data);
      const outdata = convertOutData(res.data);
      params.success(
        {
          fileid: params.fileid,
          fname: params.name,
          datanum: params.datanum,
          interval: params.interval,
          input: createRowData(indata, params.threshold, params.includeEnd),
          output: createRowData(outdata, params.threshold, params.includeEnd),
        },
        res.status
      );
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetFIleParam extends GetFIleModel, ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * 401エラーでリトライする場合はtrue
   */
  isRetry401: boolean;
}

/**
 * ファイルのチャートデータを取得します
 * @function getFiles
 * @param {IGetFIleParam} params - ファイルチャートデータ取得パラメータ
 */
export function getFile(params: IGetFIleParam) {
  const {
    authService,
    projectid,
    fileid,
    name,
    datanum,
    interval,
    threshold,
    includeEnd,
    isRetry401,
  } = params;
  const url = WebAPI.GET_DATAFILE(projectid, fileid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          fileid: fileid,
          name: name,
          datanum: datanum,
          interval: interval,
          threshold: threshold,
          includeEnd: includeEnd,
          success: params.success,
          fail: !isRetry401
            ? params.fail
            : (id, status) => {
                if (status === 401) {
                  getFileRetry401(authService, url, params, params);
                } else {
                  params.fail(id, status);
                }
              },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      fileid: fileid,
      name: name,
      datanum: datanum,
      interval: interval,
      threshold: threshold,
      includeEnd: includeEnd,
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * ファイルのチャートデータを取得します
 * @function getFileRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {GetFIleModel} model - ファイル取得モデル
 * @param {ICallbackParams} param - コールバックパラメータ
 */
function getFileRetry401(
  authService: AuthService,
  url: string,
  model: GetFIleModel,
  param: ICallbackParams
) {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        fileid: model.fileid,
        name: model.name,
        datanum: model.datanum,
        interval: model.interval,
        threshold: model.threshold,
        includeEnd: model.includeEnd,
        success: param.success,
        fail: param.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      param.fail(id, status);
    },
  });
}
